import { Select } from '@zoopla-eng/zds-components/select';
import {
    buildSelectPriceTitleToRent,
    buildSelectPriceTitleForSale
} from '@zoopla/redux-reducer-filters';
import { SelectGroup } from './SelectGroup';
import { useContext, createContext, memo, useMemo } from 'react';
import { SearchBarContext } from './SearchBar';
import { coerceQueryParameter } from '../utils/coerceQueryParameter';
import { useSearchType } from '../hooks/useSearchType';

const priceFrequencyOptions: { [key: string]: string } = {
    per_month: 'pcm',
    per_week: 'pcw'
};

const toRentPriceOptions = [
    '25',
    '50',
    '75',
    '100',
    '125',
    '150',
    '175',
    '200',
    '225',
    '250',
    '275',
    '300',
    '325',
    '350',
    '375',
    '400',
    '425',
    '450',
    '475',
    '500',
    '550',
    '600',
    '650',
    '700',
    '750',
    '800',
    '850',
    '900',
    '950',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2500',
    '3000',
    '3500',
    '4000',
    '4500',
    '5000',
    '7500',
    '8000',
    '8500',
    '9000',
    '9500',
    '10000',
    '12500',
    '15000',
    '17500',
    '20000',
    '25000'
];

const forSalePriceOptions = [
    '10000',
    '20000',
    '30000',
    '40000',
    '50000',
    '60000',
    '70000',
    '80000',
    '90000',
    '100000',
    '110000',
    '120000',
    '125000',
    '130000',
    '140000',
    '150000',
    '160000',
    '170000',
    '180000',
    '190000',
    '200000',
    '210000',
    '220000',
    '230000',
    '240000',
    '250000',
    '275000',
    '300000',
    '325000',
    '350000',
    '375000',
    '400000',
    '425000',
    '450000',
    '475000',
    '500000',
    '550000',
    '600000',
    '650000',
    '700000',
    '750000',
    '800000',
    '850000',
    '900000',
    '950000',
    '1000000',
    '1100000',
    '1200000',
    '1300000',
    '1400000',
    '1500000',
    '1600000',
    '1700000',
    '1800000',
    '1900000',
    '2000000',
    '2100000',
    '2200000',
    '2300000',
    '2400000',
    '2500000',
    '2750000',
    '3000000',
    '3250000',
    '3500000',
    '3750000',
    '4000000',
    '4250000',
    '4500000',
    '4750000',
    '5000000',
    '5500000',
    '6000000',
    '6500000',
    '7000000',
    '7500000',
    '8000000',
    '8500000',
    '9000000',
    '9500000',
    '10000000',
    '12500000',
    '15000000'
];

type PriceRangeValue = {
    priceMin: string;
    priceMax: string;
    priceFrequency: string;
};

type PriceRangeContext = {
    priceOptions: string[];
    isForSale?: boolean;
    value: PriceRangeValue;
    onChange?: (props: PriceRangeValue) => void;
    dropdownValue: string;
};

const initialContextValue: PriceRangeContext = {
    priceOptions: [],
    isForSale: undefined,
    value: {
        priceMin: '',
        priceMax: '',
        priceFrequency: ''
    },
    onChange: undefined,
    dropdownValue: ''
};

const PriceRangeContext = createContext<PriceRangeContext>(initialContextValue);

export const SelectPriceRange = memo<{
    children: React.ReactNode;
    type?: 'for-sale' | 'new-homes' | 'to-rent';
}>(({ children, type }) => {
    const { formValue, updateFormValue } = useContext(SearchBarContext);
    const derivedSearchType = useSearchType();
    const searchType = type || derivedSearchType;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const value = useMemo(
        () => ({
            priceMin: coerceQueryParameter(formValue.price_min),
            priceMax: coerceQueryParameter(formValue.price_max),
            priceFrequency: coerceQueryParameter(
                formValue.price_frequency || 'per_month'
            )
        }),
        [formValue.price_min, formValue.price_max, formValue.price_frequency]
    );

    const isForSale = ['for-sale', 'new-homes'].includes(searchType);
    const priceOptions = isForSale ? forSalePriceOptions : toRentPriceOptions;

    const dropdownValue = isForSale
        ? buildSelectPriceTitleForSale(value.priceMin, value.priceMax)
        : buildSelectPriceTitleToRent(
              value.priceMin,
              value.priceMax,
              value.priceMin,
              value.priceMax,
              value.priceFrequency
          );

    const memoizedContextValue = useMemo(() => {
        const onChange = ({
            priceMin,
            priceMax,
            priceFrequency
        }: PriceRangeValue) => {
            updateFormValue?.((oldFormValue) => ({
                ...oldFormValue,
                price_min: priceMin,
                price_max: priceMax,
                price_frequency: priceFrequency
            }));
        };

        return {
            value,
            onChange,
            dropdownValue,
            priceOptions,
            isForSale
        };
    }, [value, dropdownValue, priceOptions, isForSale, updateFormValue]);

    return useMemo(
        () => (
            <PriceRangeContext.Provider value={memoizedContextValue}>
                {children}
            </PriceRangeContext.Provider>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [value, isForSale]
    );
});

export const SelectRentalPriceFrequency = () => {
    const { value, onChange, isForSale } = useContext(PriceRangeContext);
    const frequencyOptions = [
        { id: 'per_month', text: 'Month' },
        { id: 'per_week', text: 'Week' }
    ];

    if (isForSale) {
        return null;
    }

    return (
        <Select
            options={frequencyOptions}
            onSelectedOptionChange={(o) => {
                onChange &&
                    onChange({
                        ...value,
                        priceFrequency: o.id
                    });
            }}
            selectedOption={
                frequencyOptions.find((o) => o.id === value.priceFrequency) ??
                frequencyOptions[0]
            }
            label="Price per"
            data-testid="desktop_price_frequency-filter-select"
        />
    );
};

export const SelectPriceMin = () => {
    const { value, onChange, priceOptions, isForSale } =
        useContext(PriceRangeContext);

    const priceMinOptions: string[] =
        value.priceMax !== ''
            ? priceOptions.slice(0, priceOptions.indexOf(value.priceMax) + 1)
            : priceOptions;

    const priceFrequency = priceFrequencyOptions[value.priceFrequency];

    const minPriceOptions = priceMinOptions.map((priceMinOption) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMinOption));

        const text = `${price}${!isForSale ? ` ${priceFrequency}` : ''}`;

        return {
            id: priceMinOption,
            text
        };
    });

    const minPriceDefaultOption = useMemo(
        () => ({
            id: '',
            text: 'No min'
        }),
        []
    );

    const selectedOption = useMemo(() => {
        return (
            minPriceOptions.find((o) => {
                return o.id === value.priceMin;
            }) ?? minPriceDefaultOption
        );
    }, [minPriceDefaultOption, minPriceOptions, value.priceMin]);

    return (
        <Select
            options={[minPriceDefaultOption, ...minPriceOptions]}
            onSelectedOptionChange={(o) => {
                onChange &&
                    onChange({
                        ...value,
                        priceMin: o.id
                    });
            }}
            selectedOption={selectedOption}
            label="Min price"
            data-testid="min_price"
        />
    );
};

export const SelectPriceMax = () => {
    const { value, onChange, priceOptions, isForSale } =
        useContext(PriceRangeContext);

    const priceMaxOptions =
        value.priceMin !== ''
            ? priceOptions.slice(priceOptions.indexOf(value.priceMin))
            : priceOptions;

    const priceFrequency = priceFrequencyOptions[value.priceFrequency];

    const maxPriceOptions = priceMaxOptions.map((priceMaxOption) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMaxOption));

        const text = `${price}${!isForSale ? ` ${priceFrequency}` : ''}`;

        return {
            id: priceMaxOption,
            text
        };
    });

    const maxPriceDefaultOption = useMemo(
        () => ({
            id: '',
            text: 'No max'
        }),
        []
    );

    const selectedOption = useMemo(() => {
        return (
            maxPriceOptions.find((option) => {
                return option.id === value.priceMax;
            }) ?? maxPriceDefaultOption
        );
    }, [maxPriceDefaultOption, maxPriceOptions, value.priceMax]);

    return (
        <Select
            options={[maxPriceDefaultOption, ...maxPriceOptions]}
            onSelectedOptionChange={(o) => {
                onChange &&
                    onChange({
                        ...value,
                        priceMax: o.id
                    });
            }}
            selectedOption={selectedOption}
            label="Max price"
            data-testid="max_price"
        />
    );
};

export const SelectGroupPriceRange = memo<{
    children: React.ReactNode;
    align?: 'left' | 'right';
}>(({ children, align = 'left' }) => {
    const { dropdownValue } = useContext(PriceRangeContext);

    return (
        <SelectGroup title="Price" value={dropdownValue} align={align}>
            {children}
        </SelectGroup>
    );
});
